/**
 * Customer API response parsers
 * Transform API responses to app-friendly format
 */

/**
 * Parse initiate response (signup/login initiate)
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed initiate data
 */
export const parseInitiateResponse = (response) => {
  if (!response || !response.code) {
    return {
      success: false,
      message: 'Invalid response format',
      requestId: null
    };
  }

  const { code, message, result } = response;

  return {
    success: code === 200 || code === '200',
    code: code,
    message: message || 'Request initiated successfully',
    requestId: result?.requestId || null,
    data: result || null
  };
};

/**
 * Parse confirm response (signup/login confirm)
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed confirm data with token
 */
export const parseConfirmResponse = (response) => {
  if (!response || !response.code) {
    return {
      success: false,
      message: 'Invalid response format',
      token: null
    };
  }

  const { code, message, result } = response;

  return {
    success: code === 200 || code === '200',
    code: code,
    message: message || 'Verified successfully',
    token: result?.token || null,
    data: result || null
  };
};

/**
 * Parse customer profile response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed customer profile data
 */
export const parseCustomerProfileResponse = (response) => {
  if (!response || !response?.code) {
    return {
      success: false,
      message: 'Invalid response format',
      profile: null
    };
  }

  const { code, message, result } = response;

  // Helper function to convert boolean to Yes/No
  const booleanToYesNo = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return value || '';
  };

  // Helper function to convert gender code to full text
  const formatGender = (gender) => {
    if (gender === 'M') return 'Male';
    if (gender === 'F') return 'Female';
    return gender || '';
  };

  const occupationMapping = {
    'public': 'Public Service',
    'private': 'Private Service',
    'business': 'Business',
    'student': 'Student',
    'housewife': 'Housewife',
    'agriculture': 'Agriculture',
    'retired': 'Retired',
    'others': 'Others'
  }

  // Extract personal details with optional chaining
  const personalDetails = result?.personalDetails || {};
  const customer = result?.customer || {};
  const addressDetails = result?.addressDetails || {};
  const domesticAddress = addressDetails?.domestic || {};
  const fatcaDetails = result?.fatcaDetails || {};
  return {
    success: code === 200 || code === '200',
    code: code || '',
    message: message || 'Profile retrieved successfully',
    profile: {
      // Basic customer info
      emailId: customer?.email || '',
      mobileNumber: customer?.mobile || '',
      fullName: personalDetails?.fullName || '',
      raProfile: customer?.raProfileCode || '',
      customerType: customer?.customerType || customer?.customer_type || 'REGULAR',

      // Personal details
      panCardNumber: personalDetails?.panCardNumber || '',
      aadharNumber: personalDetails?.aadharNumber || '',
      fatherName: personalDetails?.fatherName || '',
      motherName: personalDetails?.motherName || '',
      gender: formatGender(personalDetails?.gender),
      maritalStatus: personalDetails?.maritalStatus || '',
      citizenship: personalDetails?.citizenship || '',
      dateOfBirth: personalDetails?.dateOfBirth || '',

      // Address details
      addressLine1: domesticAddress?.addressLine1?.replace(/,/g, ', ') || '',
      addressLine2: domesticAddress?.addressLine2?.replace(/,/g, ', ') || '',
      addressLine3: domesticAddress?.addressLine3?.replace(/,/g, ', ') || '',
      city: domesticAddress?.city || '',
      state: domesticAddress?.state || '',
      pincode: domesticAddress?.pincode || '',

      // FATCA details
      placeOfBirth: fatcaDetails?.placeOfBirth || '',
      occupation: occupationMapping[fatcaDetails?.occupation] || '',
      politicalExposure: booleanToYesNo(fatcaDetails?.politicalExposure),
      taxResident: fatcaDetails?.taxResident || '',
      residentialStatus: fatcaDetails?.residentialStatus || '',
      annualIncome: fatcaDetails?.incomeSlab || '',

      // Fields not available in API (will remain empty)
      countryOfBirth: '',
      sourceOfIncome: '',
      permanentAddressType: ''
    },
    data: result || null
  };
};

/**
 * Parse onboarding status response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed onboarding status data
 */
export const parseOnboardingStatusResponse = (response) => {
  // Handle nested response structure (response.data.code) or direct response
  const actualResponse = response?.data || response;

  if (!actualResponse || (!actualResponse.code && !actualResponse.result)) {
    return {
      success: false,
      message: 'Invalid response format',
      onboardingStatus: null
    };
  }

  const { code, message, result } = actualResponse;

  // Check if code is success (can be number 200 or string like 'OBDS0001')
  const isSuccess = code === 200 || code === '200' ||
    (typeof code === 'string' && code.startsWith('OBDS'));

  console.log("isSuccess", result);
  return {
    success: isSuccess,
    code: code || null,
    message: message || 'Onboarding status retrieved successfully',
    onboardingStatus: result || null,
    data: result || null
  };
};

/**
 * Parse customer error response
 * @param {Object} error - Raw error response
 * @returns {Object} Parsed error data
 */
export const parseCustomerErrorResponse = (error) => {
  if (!error) {
    return {
      success: false,
      message: 'Unknown error occurred',
      code: 'UNKNOWN_ERROR',
      details: null
    };
  }

  const { data, status } = error;

  // Handle both API error format and HTTP error format
  if (data?.code) {
    return {
      success: false,
      code: data.code,
      message: data.message || 'An error occurred',
      details: data.details || null
    };
  }

  return {
    success: false,
    message: data?.message || data?.error || 'An error occurred',
    code: data?.code || `HTTP_${status}`,
    details: data?.details || null,
    status: status || 500
  };
};

